import { db, storage } from "@firebase";
import { COLLECTIONS } from "constant";
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  writeBatch,
  where,
  query,
  collection,
  getDocs,
  deleteDoc,
  Timestamp,
  DocumentReference,
} from "firebase/firestore";
import { throwException } from "utils";
import { Cart, CartItem, Content } from "../types/types";
class CartContent {
  // Add an item to the cart

  async addToCart(
    userId: string,
    contentId: string,
    subTotal: number,
    tax: number,
    status: string,
    contentData: {
      title: string;
      image: string;
      price: number;
      category: string;
      type: string;
      quantity: number;
      afterCoupon: number;
    }
  ) {
    try {
      const userRef = doc(db, COLLECTIONS.USER, userId);

      // Step 1: Query for a pending cart
      const cartQuery = query(
        collection(db, COLLECTIONS.CART),
        // where("userId", "==", doc(db, COLLECTIONS.USER, userId)),
        where("userId", "==", userRef),
        where("status", "==", "pending")
      );

      const existingCart = await getDocs(cartQuery);
      let cartId: string;

      if (!existingCart.empty) {
        // Use the existing pending cart
        cartId = existingCart.docs[0].id;
      } else {
        // Create a new cart with a pending status
        const cartRef = doc(collection(db, COLLECTIONS.CART));
        const cartData = {
          // userId: doc(db, COLLECTIONS.USER, userId),
          userId: userRef,
          subTotal: 0,
          tax: 0,
          status: "pending",
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        };
        await setDoc(cartRef, cartData);
        cartId = cartRef.id;
      }

      // Step 2: Add item to the cart
      const cartItemQuery = query(
        collection(db, COLLECTIONS.CART_ITEMS),
        where("cartId", "==", doc(db, COLLECTIONS.CART, cartId)),
        where("contentId", "==", doc(db, COLLECTIONS.CONTENTS, contentId))
      );

      const existingItem = await getDocs(cartItemQuery);

      if (!existingItem.empty) {
        return existingItem.docs[0].id; // Return the existing item ID
      }

      // Add new item to cart items
      const cartItemRef = doc(collection(db, COLLECTIONS.CART_ITEMS));
      const cartItemData = {
        cartId: doc(db, COLLECTIONS.CART, cartId),
        contentId: doc(db, COLLECTIONS.CONTENTS, contentId),
        quantity: contentData.quantity,
        afterCoupon: contentData.afterCoupon,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };
      await setDoc(cartItemRef, cartItemData);

      console.log("Cart item added successfully");
      return cartItemRef.id;
    } catch (error) {
      console.error("Error adding to cart:", error);
      throw error;
    }
  }

  // Get cart items
  async getCartItems(userId: string) {
    try {
      const userRef = doc(db, COLLECTIONS.USER, userId);
      const cartQuery = query(
        collection(db, COLLECTIONS.CART),
        // where("userId", "==", doc(db, COLLECTIONS.USER, userId)),
        where("userId", "==", userRef),
        where("status", "==", "pending")
      );

      const cartSnapshot = await getDocs(cartQuery);
      if (cartSnapshot.empty) {
        // console.warn("No cart found for user");
        return { cartId: null, items: [] };
      }

      const cartDoc = cartSnapshot.docs[0];

      const cartItemQuery = query(
        collection(db, COLLECTIONS.CART_ITEMS),
        // where("cartId", "==", cartDoc.ref)
        where("cartId", "==", doc(db, COLLECTIONS.CART, cartDoc.id))
      );

      const cartItemsSnapshot = await getDocs(cartItemQuery);

      // Map and resolve `contentData`
      const cartItems = await Promise.all(
        cartItemsSnapshot.docs.map(async (itemDoc) => {
          const itemData = itemDoc.data();
          let contentData = null;

          if (
            itemData.contentId &&
            itemData.contentId instanceof DocumentReference
          ) {
            const contentSnapshot = await getDoc(itemData.contentId);
            contentData = contentSnapshot.exists()
              ? contentSnapshot.data()
              : null;
          }

          return {
            id: itemDoc.id,
            ...itemData,
            contentData,
          };
        })
      );

      return { cartId: cartDoc.id, items: cartItems };
    } catch (error) {
      console.error("Error fetching cart items:", error);
      throw error;
    }
  }

  // countity update

  async updateCartItemQuantity(itemId: string, quantity: number) {
    try {
      const itemRef = doc(db, COLLECTIONS.CART_ITEMS, itemId);
      await updateDoc(itemRef, { quantity, updatedAt: Timestamp.now() });
    } catch (error) {
      console.error("Error updating cart item quantity:", error);
      throw error;
    }
  }

  // Update an existing cart item
  async updateCart(cartId: string, updatedData: Partial<Cart>) {
    try {
      // Correct reference to the CART collection
      const cartRef = doc(db, COLLECTIONS.CART, cartId);

      // Perform the update
      await updateDoc(cartRef, { ...updatedData, updatedAt: Timestamp.now() });
    } catch (error) {
      console.error("Error updating cart:", error);
      throw error;
    }
  }

  // Retrieve cart data for a specific user
  async getCartByUser(userId: string) {
    const cartQuery = query(
      collection(db, COLLECTIONS.CART),
      where("userId", "==", doc(db, COLLECTIONS.USER, userId)),
      where("status", "==", "pending") // Only fetch pending carts
    );
    const snapshot = await getDocs(cartQuery);
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  }

  // Delete a cart item
  async deleteCart(itemId: string) {
    try {
      const itemRef = doc(db, COLLECTIONS.CART_ITEMS, itemId);
      await deleteDoc(itemRef);
      console.log("Cart item deleted successfully");
    } catch (error) {
      console.error("Error deleting cart item:", error);
      throw error;
    }
  }
}

const CartService = new CartContent();

export { CartService };
